/* Shared body scroll lock for every overlay layer (Founding Members sheet,
   cart drawer, header drawer). One refcounted lock, keyed on the DOCUMENT
   (documentElement.dataset), never on an element instance — global.js
   replaces <cart-drawer> wholesale on some cart updates, and an
   instance-keyed flag dies with the replaced element, orphaning the lock
   (body stuck at position:fixed; top:-Npx = the "header detached" freeze).
   On iOS Safari body{overflow:hidden} does not stop touch panning, so
   mobile uses the fixed-body pattern; desktop keeps overflow-only. */
(() => {
  'use strict';

  const html = document.documentElement;
  const owners = new Set();
  const isMobile = () => window.matchMedia('(max-width: 991px)').matches;

  /* The fixed-body technique stops the document scrolling but it does NOT stop
     iOS rubber-band panning of the locked page, and that pan is what drives
     Safari's toolbar collapse/expand. Cancel touchmove for as long as a layer
     is open, except inside a scroller that can still move in the gesture's
     direction - .fm-modal__body, .drawer__content and the nav panel keep
     working, pinch-zoom and horizontal rails are never touched. */
  let touchY = 0;
  let touchX = 0;

  const scrollableAncestor = (node, dy) => {
    let el = node instanceof Element ? node : null;
    for (; el && el !== document.body; el = el.parentElement) {
      const style = getComputedStyle(el);
      if (!/(auto|scroll)/.test(style.overflowY)) continue;
      if (el.scrollHeight - el.clientHeight <= 1) continue;
      // dy < 0 = finger down = the scroller must have room above it
      if (dy < 0 && el.scrollTop > 0) return el;
      if (dy > 0 && el.scrollTop < el.scrollHeight - el.clientHeight - 1) return el;
    }
    return null;
  };

  const onTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    touchY = e.touches[0].clientY;
    touchX = e.touches[0].clientX;
  };

  const onTouchMove = (e) => {
    if (e.touches.length > 1) return;
    const dy = touchY - e.touches[0].clientY;
    const dx = touchX - e.touches[0].clientX;
    if (Math.abs(dx) > Math.abs(dy)) return;
    if (scrollableAncestor(e.target, dy)) return;
    if (e.cancelable) e.preventDefault();
  };

  /* :has() in a querySelector string throws on browsers that do not support it,
     which would take the whole lock down - walk it by hand instead */
  const stickyHeader = () => {
    const section = document.querySelector('.section-header-benchmark');
    return section && section.querySelector('.header-benchmark--sticky') ? section : null;
  };

  const clearFixedStyles = () => {
    html.style.overflow = '';
    delete html.dataset.bmScrollLockHeader;
    html.style.removeProperty('--bm-scroll-lock-shift');
    document.removeEventListener('touchstart', onTouchStart);
    document.removeEventListener('touchmove', onTouchMove);
    document.body.classList.remove('bm-scroll-locked');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
  };

  const apply = () => {
    if (html.dataset.bmScrollLock) return;
    if (isMobile()) {
      const y = window.scrollY;
      /* a fixed body cannot host a position:sticky child, so the header and its
         black sale bar dropped off the top of the screen on every overlay open
         and snapped back on close. Measure where the header sits now, switch it
         to the relative rule (header-benchmark.css), then measure where it
         landed and hand the difference back as a paint-only offset - measured
         rather than assumed because sections above it (announcement bar) move
         the delta. */
      const header = stickyHeader();
      const headerTop = header ? header.getBoundingClientRect().top : 0;
      html.dataset.bmScrollLock = 'fixed:' + y;
      if (header) html.dataset.bmScrollLockHeader = '1';
      html.style.overflow = 'hidden';
      /* deliberately NO overflow:hidden on <body>. html{overflow:hidden} stops
         the root overflow propagating to the viewport, so a body overflow is
         honoured on the body box itself and turns <body> into a scroll
         container. The sticky header (.section-header-benchmark) then takes
         that fixed, -Npx-shifted body as its scrollport and paints N pixels
         above the screen, carrying the hamburger and its panel off the top:
         the menu opens and is simply not on screen. position:fixed plus the
         touchmove cancel above is the whole scroll stop; the body overflow
         was pure redundancy. */
      document.body.style.position = 'fixed';
      document.body.style.top = `-${y}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      if (header) {
        /* Paul (Aug 24): on iOS the menu sometimes opened to a bare scrim with
           the panel off screen, deep down a long page (the mission page).
           Re-reading the header's rect right after mutating <body> is the
           fragile step: that read forces a synchronous reflow, and Safari can
           answer with a pre-mutation box while momentum is still settling or
           the URL bar is animating - exactly the moment you tap after a long
           scroll. A wrong shift parks the header, and the panel hanging off
           it, above the viewport.

           Where it lands is knowable without measuring: <body> is pinned at
           -y, so the header sits at its document position minus y. Compute
           that, then verify once on the next frame and correct only if it is
           genuinely off screen, so a bad first answer self-heals. */
        const landed = header.offsetTop - y;
        const shift = headerTop - landed;
        if (shift) html.style.setProperty('--bm-scroll-lock-shift', `${shift}px`);
        requestAnimationFrame(() => {
          if (html.dataset.bmScrollLock !== 'fixed:' + y) return;
          const top = header.getBoundingClientRect().top;
          if (Math.abs(top - headerTop) < 2) return;
          const corrected = shift + (headerTop - top);
          html.style.setProperty('--bm-scroll-lock-shift', `${corrected}px`);
        });
      }
      document.addEventListener('touchstart', onTouchStart, { passive: true });
      document.addEventListener('touchmove', onTouchMove, { passive: false });
    } else {
      html.dataset.bmScrollLock = 'overflow:0';
      document.body.style.overflow = 'hidden';
    }
  };

  const release = () => {
    const state = html.dataset.bmScrollLock;
    if (!state) return;
    delete html.dataset.bmScrollLock;
    const [mode, yRaw] = state.split(':');
    if (mode === 'fixed') {
      /* the {behavior:'instant'} object form still lets Safari present one
         composited frame at scrollTop 0 before the restore lands, which reads as
         a whole-page flash on close; the legacy two-arg form is synchronous */
      const target = parseInt(yRaw, 10) || 0;
      const behavior = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      clearFixedStyles();
      window.scrollTo(0, target);
      html.style.scrollBehavior = behavior;
    } else {
      document.body.style.overflow = '';
    }
  };

  window.BMScrollLock = {
    lock(owner) {
      owners.add(owner);
      apply();
    },
    unlock(owner) {
      owners.delete(owner);
      if (!owners.size) release();
    },
    // Recovery pass for restored/reloaded pages: bfcache brings a page back
    // with its inline styles and JS heap exactly as they were (e.g. swiping
    // back from checkout), and nothing else re-checks lock state. When no
    // layer is actually open, drop any lock this utility holds, clear a
    // legacy fixed-body leak from the pre-shared-lock cart code, and remove
    // global.js's leaked overflow-hidden-tablet class.
    sanitize() {
      // GAH-RANGE-01: the PDP's PracticeCam pop-out can be open on the first
      // pageshow, which fires at window load, not only on bfcache restores
      const anyOpen = document.querySelector('cart-drawer.active, cart-drawer.is-closing')
        || document.querySelector('[data-fm-modal]:not([hidden])')
        || document.querySelector('.header-benchmark__drawer[open]')
        || document.querySelector('[data-practicecam-pop][open]');
      if (!anyOpen) {
        owners.clear();
        release();
        if (document.body.style.position === 'fixed' && document.body.style.top) {
          const y = Math.abs(parseInt(document.body.style.top, 10)) || 0;
          clearFixedStyles();
          window.scrollTo(0, y);
        }
      }
      if (!document.querySelector('.header-benchmark__drawer[open]')) {
        document.body.classList.remove('overflow-hidden-tablet');
      }
    },
  };

  window.addEventListener('pageshow', () => window.BMScrollLock.sanitize());
  document.addEventListener('DOMContentLoaded', () => window.BMScrollLock.sanitize());
})();
